import {
  createContext,
  forwardRef,
  useContext,
  useId,
  useRef,
  useState,
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Spinner } from "@sfc/ui";
import { useEffectiveUser } from "@client/hooks/use-effective-user";
import { useImpersonationRefValue } from "@client/ImperativeState";
import {
  type RoundEntryViewer,
  prepareRoundEntry,
} from "../round/prepareRoundEntry";

const RoundOpeningContext = createContext<{
  pendingLinkId: string | null;
  open: (
    event: MouseEvent<HTMLAnchorElement>,
    roundId: string,
    href: string,
    linkId: string,
  ) => void;
} | null>(null);

export function RoundOpeningProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { data: user } = useEffectiveUser();
  const viewAsUserId = useImpersonationRefValue();
  const [pending, setPending] = useState<{
    linkId: string;
    viewer: RoundEntryViewer;
  } | null>(null);
  const request = useRef(0);
  const viewer = useRef({ userId: user?.userId ?? "", viewAsUserId });
  viewer.current = { userId: user?.userId ?? "", viewAsUserId };

  function open(
    event: MouseEvent<HTMLAnchorElement>,
    roundId: string,
    href: string,
    linkId: string,
  ) {
    // Modified clicks and new tabs retain native link behavior.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (event.currentTarget.target && event.currentTarget.target !== "_self") ||
      event.currentTarget.hasAttribute("download") ||
      !user
    )
      return;
    event.preventDefault();
    const anchor = event.currentTarget;
    const entryLocation = window.location.href;
    const currentRequest = ++request.current;
    const entryViewer = viewer.current;
    setPending({ linkId, viewer: entryViewer });
    void (async () => {
      try {
        const destination = new URL(href, window.location.origin);
        await prepareRoundEntry(
          queryClient,
          roundId,
          entryViewer,
          destination.pathname.replace(/^\/round\/[^/]+/, ""),
          destination.search,
        );
      } catch {
        // Let Round's existing retry/error UI handle unavailable or denied
        // rounds. Preparing the navigation must not create another error path.
      }
      if (currentRequest !== request.current) return;
      // Completion ends this interaction even when navigation was abandoned.
      // A superseded request must not clear the newer link's indicator.
      setPending(null);
      if (
        !anchor.isConnected ||
        window.location.href !== entryLocation ||
        viewer.current.userId !== entryViewer.userId ||
        viewer.current.viewAsUserId !== entryViewer.viewAsUserId
      )
        return;
      navigate(href);
    })();
  }

  return (
    <RoundOpeningContext.Provider
      value={{
        pendingLinkId:
          pending?.viewer.userId === user?.userId &&
          pending?.viewer.viewAsUserId === viewAsUserId
            ? pending.linkId
            : null,
        open,
      }}
    >
      {children}
    </RoundOpeningContext.Provider>
  );
}

export const RoundEntryLink = forwardRef<
  HTMLAnchorElement,
  ComponentProps<"a"> & { roundId: string }
>(function RoundEntryLink(
  { roundId, href = "", children, onClick, className = "", ...props },
  ref,
) {
  const opening = useContext(RoundOpeningContext);
  const linkId = useId();
  const pending = opening?.pendingLinkId === linkId;
  return (
    <a
      {...props}
      ref={ref}
      href={href}
      className={`group/round-entry ${className}`}
      aria-busy={pending || undefined}
      data-pending={pending || undefined}
      onClick={(event) => {
        onClick?.(event);
        opening?.open(event, roundId, href, linkId);
      }}
    >
      {children}
      {pending && (
        <Spinner
          className="size-4 shrink-0 text-text-muted motion-reduce:animate-none"
          aria-label="Opening round"
        />
      )}
    </a>
  );
});
